import { useMutation, useQuery } from '@tanstack/react-query'
import { apiClient } from '@/core/api/apiClient'
import { PrivateClientFinancingProfile } from '@otw/models'
import { useAuth } from 'hooks/auth/useAuth'

export const useClientPrivateFinancingProfile = () => {
  const { currentUser } = useAuth()

  // Create financing profile mutation
  const createFinancingProfile = useMutation({
    mutationFn: async (
      profile: Omit<PrivateClientFinancingProfile, 'id' | 'date_created' | 'date_modified' | 'user_id'>,
    ) => {
      if (!currentUser?.uid) {
        throw new Error('User not authenticated')
      }

      const now = new Date().toISOString()
      const completeProfile: Omit<PrivateClientFinancingProfile, 'id'> = {
        date_created: now,
        date_modified: now,
        user_id: currentUser.uid,
        ...profile,
      }

      const response = await apiClient.portal.client.createClientPrivateFinancingProfile(completeProfile)
      
      // The API returns { success: true } without data, so we return the profile we created
      return completeProfile
    },
  })

  // Get financing profile query
  const {
    data: financingProfile,
    isLoading: isLoadingProfile,
    error: profileError,
    refetch: refetchProfile,
  } = useQuery({
    queryKey: ['clientFinancingProfile', currentUser?.uid],
    queryFn: async (): Promise<PrivateClientFinancingProfile | null> => {
      if (!currentUser?.uid) {
        throw new Error('User not authenticated')
      }

      const response = await apiClient.portal.client.getClientPrivateFinancingProfile()

      // The API returns { success: true, data: profile }, so we need to access response.data
      return response.data as PrivateClientFinancingProfile
    },
    enabled: !!currentUser?.uid,
  })

  return {
    // Create profile
    createFinancingProfile: createFinancingProfile.mutate,
    isCreating: createFinancingProfile.isPending,
    createError: createFinancingProfile.error,

    // Get profile
    financingProfile,
    isLoadingProfile,
    profileError,
    refetchProfile,
  }
}
